import React, { useEffect, useState } from 'react';
import { timeAPI, userAPI, taskTimeAPI } from '../services/api';
import useAuthStore from '../store/authStore';
import Layout from '../components/Layout';
import OperationsHeadTimeTracking from './OperationsHeadTimeTracking';
import './TimeTracking.css';

const TimeTracking = () => {
  const { user } = useAuthStore();

  // If role is Operations Head, return the special analytics-driven view
  if (user?.role === 'Operations Head') {
    return <OperationsHeadTimeTracking />;
  }
  const [activeSession, setActiveSession] = useState(null);
  const [stats, setStats] = useState({ today: { seconds: 0, logs: 0 }, week: { seconds: 0 } });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [viewMode, setViewMode] = useState('my'); // 'my' or 'team'
  const [teamMembers, setTeamMembers] = useState([]);
  const [filters, setFilters] = useState({ userId: '' });

  const canViewTeam = ['Admin', 'Project Manager', 'Operations Head', 'Project Lead'].includes(user?.role);

  useEffect(() => {
    loadStatus();
    loadHistory();
    if (canViewTeam) loadTeamMembers();

    const clock = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(clock);
  }, []);

  useEffect(() => {
    let interval;
    if (activeSession) {
      const start = new Date(activeSession.startTime).getTime();
      const updateTimer = () => {
        const now = Date.now();
        const diff = Math.floor((now - start) / 1000);
        setElapsedSeconds(diff > 0 ? diff : 0);
      };
      updateTimer();
      interval = setInterval(updateTimer, 1000);
    } else {
      setElapsedSeconds(0);
    }
    return () => clearInterval(interval);
  }, [activeSession]);

  useEffect(() => {
    if (!loading) loadHistory();
  }, [viewMode, filters.userId]);

  const loadStatus = async () => {
    try {
      const res = await timeAPI.getStatus();
      if (res.data.success) {
        setStats({ today: res.data.data.today, week: res.data.data.week });
      }
      const taskRes = await taskTimeAPI.getActiveTask();
      if (taskRes.data.success && taskRes.data.data) {
        setActiveSession(taskRes.data.data);
      } else {
        setActiveSession(null);
      }
    } catch (error) {
      console.error('Status Load Failed:', error);
    }
  };

  const loadHistory = async () => {
    try {
      setLoading(true);
      const res = viewMode === 'my'
        ? await taskTimeAPI.getMyLogs()
        : await taskTimeAPI.getTeamLogs();

      if (res.data.success) {
        let filteredLogs = res.data.data || [];
        if (viewMode === 'team' && filters.userId) {
          filteredLogs = filteredLogs.filter(l => l.user?._id === filters.userId);
        }
        setLogs(filteredLogs);
      }
    } catch (error) {
      console.error('History Load Failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const res = await userAPI.getAll();
      if (res.data.success) setTeamMembers(res.data.data);
    } catch (err) {
      console.error("Failed to load team", err);
    }
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const formatHours = (seconds) => (seconds / 3600).toFixed(2) + 'h';

  return (
    <Layout>
      <div className="time-tracking-new">
        <header className="time-header">
          <div className="header-left">
            <h1>Time Tracking</h1>
            <p>Session-based work tracking across your teams</p>
          </div>
          <div className="current-date-pill">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
            <span style={{ fontWeight: 600 }}>{currentTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} • {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        </header>

        {/* Active Session Marshmallow */}
        {activeSession && (
          <div className="active-session-marshmallow">
            <div className="active-content">
              <div className="pulse-marshmallow"></div>
              <div>
                <p style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Ongoing Session</p>
                <span className="active-task-label">{activeSession.task?.title || 'Active Session'}</span>
              </div>
            </div>
            <div className="active-timer-big">
              {formatTime(elapsedSeconds)}
            </div>
          </div>
        )}

        {/* Puffy Stats Grid - Aligned with Dashboard Gradations */}
        <div className="stats-row-marshmallow">
          <div className="stat-puffy-card purple">
            <span>Daily Progress</span>
            <span>{formatHours(stats.today.seconds)}</span>
          </div>
          <div className="stat-puffy-card green">
            <span>Weekly Summary</span>
            <span>{formatHours(stats.week.seconds)}</span>
          </div>
          <div className="stat-puffy-card amber">
            <span>Work Sessions</span>
            <span>{stats.today.logs}</span>
          </div>
        </div>

        {/* Logs Container - Marshmallow Style */}
        <section className="logs-container-marshmallow">
          <div className="logs-marsh-header">
            <h2>Tracked History</h2>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              {canViewTeam && viewMode === 'team' && (
                <select
                  className="marsh-select"
                  value={filters.userId}
                  onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
                >
                  <option value="">Full Team View</option>
                  {teamMembers.map(m => (
                    <option key={m._id} value={m._id}>{m.name}</option>
                  ))}
                </select>
              )}
              {canViewTeam && (
                <div className="toggle-marsh">
                  <button
                    className={`toggle-btn-marsh ${viewMode === 'my' ? 'active' : ''}`}
                    onClick={() => setViewMode('my')}
                  >My Work</button>
                  <button
                    className={`toggle-btn-marsh ${viewMode === 'team' ? 'active' : ''}`}
                    onClick={() => setViewMode('team')}
                  >Team Insights</button>
                </div>
              )}
            </div>
          </div>

          <div style={{ padding: '0 8px 8px' }}>
            {loading ? (
              <div style={{ padding: '60px', textAlign: 'center', opacity: 0.6, fontWeight: 600, color: '#7c3aed' }}>Analyzing logs...</div>
            ) : logs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8', fontStyle: 'italic' }}>
                <p>No activity records available for this period.</p>
              </div>
            ) : (
              <table className="marsh-table">
                <tbody>
                  {logs.map(log => (
                    <tr key={log._id} className="marsh-row">
                      <td>
                        <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>{log.task?.title || 'Unknown Task'}</div>
                        <div style={{ fontSize: '12px', color: '#7c3aed', fontWeight: 500 }}>{log.project?.name || 'Unassigned'}</div>
                      </td>
                      <td>
                        <div className="member-chip">
                          <div className="avatar-marsh">{log.user?.name?.charAt(0).toUpperCase()}</div>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>{log.user?.name || 'Staff'}</span>
                        </div>
                      </td>
                      <td style={{ color: '#6b7280', fontWeight: 500 }}>
                        {new Date(log.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {log.endTime && ` — ${new Date(log.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600, fontSize: '16px', color: '#1f2937' }}>
                        {formatHours(log.duration)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span className={`pill-marsh ${log.status === 'Running' ? 'running' : 'done'}`}>
                          {log.status === 'Running' ? 'Tracking' : 'Logged'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default TimeTracking;
